/**
 * Career Intelligence — Phase 2
 *
 * 3 onglets :
 *  1. Plan de carrière       — pour un employé, top postes compatibles + roadmap
 *  2. Gap Analysis           — détaillé : employé × poste cible
 *  3. Matrice compétences    — heatmap collaborateurs × compétences
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Brain, Target, Compass, Layers, AlertTriangle, CheckCircle2,
  TrendingUp, Sparkles, Users, ArrowRight, Award,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import AIInsightModal from '../components/career/AIInsightModal';

// ─────────────────────────────────────────────────────────────
// Heatmap cell color
// ─────────────────────────────────────────────────────────────
const levelColor = (lvl) => {
  if (!lvl || lvl === 0) return 'bg-slate-100 text-slate-300';
  if (lvl === 1) return 'bg-red-100 text-red-700';
  if (lvl === 2) return 'bg-orange-100 text-orange-700';
  if (lvl === 3) return 'bg-amber-100 text-amber-800';
  if (lvl === 4) return 'bg-emerald-100 text-emerald-700';
  return 'bg-emerald-200 text-emerald-900';
};

const importanceColor = (imp) =>
  imp === 'critical'
    ? 'bg-red-50 text-red-700 border-red-200'
    : imp === 'important'
    ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-slate-50 text-slate-700 border-slate-200';

// ─────────────────────────────────────────────────────────────
// Plan de carrière (suggestions)
// ─────────────────────────────────────────────────────────────
function CareerPlanTab({ employees, onShowGap, onAIEmployee, isAdmin }) {
  const [selectedEmp, setSelectedEmp] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (employees.length > 0 && !selectedEmp) setSelectedEmp(employees[0].id);
  }, [employees, selectedEmp]);

  const fetch = useCallback(async () => {
    if (!selectedEmp) return;
    try {
      setLoading(true);
      const res = await api.get(`/career-intel/career-suggestions/${selectedEmp}?limit=10`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [selectedEmp]);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div className="space-y-4" data-testid="tab-career-plan">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm font-medium text-slate-700">Employé :</span>
        <Select value={selectedEmp} onValueChange={setSelectedEmp}>
          <SelectTrigger className="w-[320px]" data-testid="career-plan-emp-select">
            <SelectValue placeholder="Sélectionnez un employé" />
          </SelectTrigger>
          <SelectContent>
            {employees.map(e => (
              <SelectItem key={e.id} value={e.id}>
                {e.firstName} {e.lastName} — {e.jobTitle || '—'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isAdmin && selectedEmp && (
          <Button size="sm" variant="outline" onClick={() => onAIEmployee(selectedEmp)} data-testid="ai-employee-btn"
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />Analyse IA
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-8">Calcul en cours...</p>
      ) : !data ? null : (data.suggestions || []).length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-500">
          <Compass className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          Aucune suggestion. Évaluez les compétences de l'employé pour activer le moteur.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.suggestions.map(s => (
            <Card key={s.profileId} className="hover:shadow-md transition-shadow" data-testid={`suggestion-${s.profileId}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{s.profileName}</p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {s.criticalBlocking && (
                        <Badge className="text-xs bg-red-100 text-red-700 border-red-200">
                          <AlertTriangle className="w-3 h-3 mr-1" />Bloquant
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {s.totalSkills} compétences
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mt-3">
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-600">Readiness</span>
                      <strong className={s.readiness >= 75 ? 'text-emerald-600' : s.readiness >= 50 ? 'text-amber-600' : 'text-red-600'}>
                        {s.readiness}%
                      </strong>
                    </div>
                    <Progress value={s.readiness} className="h-1.5" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-600">Éligibilité</span>
                      <strong className="text-slate-900">{s.eligibility}%</strong>
                    </div>
                    <Progress value={s.eligibility} className="h-1.5" />
                  </div>
                </div>

                {(s.missingSkills || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-500 mb-1">À acquérir :</p>
                    <div className="flex flex-wrap gap-1">
                      {s.missingSkills.slice(0, 4).map((sk, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                          {sk}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-3"
                  onClick={() => onShowGap(selectedEmp, s.profileId)}
                  data-testid={`gap-btn-${s.profileId}`}
                >
                  <Target className="w-3.5 h-3.5 mr-2" />
                  Voir le détail
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Gap Analysis
// ─────────────────────────────────────────────────────────────
function GapAnalysisTab({ employees, profiles, initialEmp, initialProfile, onAIDevPlan, isAdmin }) {
  const [emp, setEmp] = useState(initialEmp || '');
  const [target, setTarget] = useState(initialProfile || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialEmp) setEmp(initialEmp);
    if (initialProfile) setTarget(initialProfile);
  }, [initialEmp, initialProfile]);

  useEffect(() => {
    if (!emp || !target) { setData(null); return; }
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/career-intel/gap-analysis/${emp}/${target}`);
        setData(res.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Erreur');
        setData(null);
      } finally { setLoading(false); }
    })();
  }, [emp, target]);

  return (
    <div className="space-y-4" data-testid="tab-gap-analysis">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Employé</label>
          <Select value={emp} onValueChange={setEmp}>
            <SelectTrigger data-testid="gap-emp-select"><SelectValue placeholder="Sélectionnez un employé" /></SelectTrigger>
            <SelectContent>
              {employees.map(e => (
                <SelectItem key={e.id} value={e.id}>
                  {e.firstName} {e.lastName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Poste cible</label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid="gap-profile-select"><SelectValue placeholder="Sélectionnez un poste cible" /></SelectTrigger>
            <SelectContent>
              {profiles.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-8">Analyse en cours...</p>
      ) : !data ? (
        <p className="text-sm text-slate-400 text-center py-8">Sélectionnez un employé et un poste cible</p>
      ) : (
        <div className="space-y-4">
          {/* Header scores */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Readiness pondérée</p>
                  <p className={`text-3xl font-bold ${data.readiness >= 75 ? 'text-emerald-600' : data.readiness >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                    {data.readiness}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Éligibilité brute</p>
                  <p className="text-3xl font-bold text-slate-900">{data.eligibility}%</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Compétences maîtrisées</p>
                  <p className="text-3xl font-bold text-emerald-600">{data.mastered.length}/{data.totalSkills}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Écarts critiques</p>
                  <p className={`text-3xl font-bold ${data.criticalGaps.length > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                    {data.criticalGaps.length}
                  </p>
                </div>
              </div>

              {data.criticalBlocking && (
                <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">
                    <strong>Compétences critiques bloquantes</strong> — Ces compétences sont indispensables pour le poste {data.targetProfile.name}. Une formation ciblée est recommandée avant toute mobilité.
                  </p>
                </div>
              )}

              {isAdmin && (
                <div className="mt-4 flex justify-end">
                  <Button size="sm" onClick={() => onAIDevPlan(emp, target)} data-testid="ai-devplan-btn"
                    className="bg-indigo-600 hover:bg-indigo-700">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />Générer plan de développement (IA)
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mastered + Gaps en colonnes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Maîtrisées ({data.mastered.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.mastered.length === 0 ? (
                  <p className="text-sm text-slate-400">Aucune compétence maîtrisée</p>
                ) : data.mastered.map(s => (
                  <div key={s.skillId} className="flex items-center justify-between p-2 rounded bg-emerald-50">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{s.skillName}</p>
                      <p className="text-xs text-slate-500">Niveau {s.currentLevel} / requis {s.requiredLevel}</p>
                    </div>
                    <Badge variant="outline" className={importanceColor(s.importance)}>
                      {s.importance === 'critical' ? 'Critique' : s.importance === 'important' ? 'Important' : 'Normal'}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Target className="w-5 h-5 text-red-600" />
                  À acquérir ({data.gaps.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.gaps.length === 0 ? (
                  <p className="text-sm text-emerald-600">✓ Toutes les compétences requises sont acquises</p>
                ) : data.gaps.map(s => (
                  <div key={s.skillId} className="flex items-center justify-between p-2 rounded bg-red-50">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{s.skillName}</p>
                      <p className="text-xs text-slate-500">
                        Niveau actuel {s.currentLevel} → cible {s.requiredLevel}
                        {' · '}<span className="text-red-700 font-medium">+{s.gap}</span>
                      </p>
                    </div>
                    <Badge variant="outline" className={importanceColor(s.importance)}>
                      {s.importance === 'critical' ? 'Critique' : s.importance === 'important' ? 'Important' : 'Normal'}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Skill Matrix (heatmap)
// ─────────────────────────────────────────────────────────────
function SkillMatrixTab({ departments, families, profiles }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    department_id: 'all', family_id: 'all', job_profile_id: 'all',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all') params.append(k, v); });
      params.append('limit_skills', '40');
      const res = await api.get(`/career-intel/skill-matrix?${params}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const skills = data?.skills || [];
  const employees = data?.employees || [];
  const matrix = data?.matrix || {};
  const coverage = data?.coverage || {};

  return (
    <div className="space-y-4" data-testid="tab-matrix">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filters.department_id} onValueChange={(v) => setFilters({ ...filters, department_id: v })}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Département" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous départements</SelectItem>
            {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.family_id} onValueChange={(v) => setFilters({ ...filters, family_id: v })}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Famille métier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes familles</SelectItem>
            {families.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.job_profile_id} onValueChange={(v) => setFilters({ ...filters, job_profile_id: v })}>
          <SelectTrigger className="w-[260px]"><SelectValue placeholder="Fiche de poste" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes fiches de poste</SelectItem>
            {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-8">Chargement de la matrice...</p>
      ) : skills.length === 0 || employees.length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-500">
          <Layers className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          {employees.length === 0
            ? "Aucun employé pour ces filtres"
            : "Aucune compétence évaluée pour ce périmètre. Lancez une campagne pour activer la matrice."}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {employees.length} collaborateur{employees.length > 1 ? 's' : ''} × {skills.length} compétence{skills.length > 1 ? 's' : ''}
            </CardTitle>
            <CardDescription>
              {data?.totals?.assessments || 0} évaluations totales · cellules colorées par niveau (0 = gris, 5 = vert vif)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse" data-testid="skill-matrix-table">
                <thead>
                  <tr>
                    <th className="text-left p-2 sticky left-0 bg-white z-10 min-w-[180px] border-b border-slate-200 font-medium text-slate-700">
                      Collaborateur
                    </th>
                    {skills.map(s => (
                      <th
                        key={s.id}
                        className="p-2 border-b border-slate-200 font-medium text-slate-700"
                        title={s.name}
                      >
                        <div className="-rotate-45 origin-bottom-left whitespace-nowrap" style={{ height: 80, width: 24 }}>
                          {s.name.length > 20 ? s.name.substring(0, 20) + '…' : s.name}
                        </div>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    <td className="text-xs p-2 sticky left-0 bg-slate-50 font-medium text-slate-500">
                      Couverture (%)
                    </td>
                    {skills.map(s => (
                      <td key={s.id} className="text-xs p-1 text-center bg-slate-50 text-slate-500">
                        {coverage[s.id] || 0}
                      </td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map(e => (
                    <tr key={e.id} className="hover:bg-slate-50">
                      <td className="p-2 sticky left-0 bg-white border-b border-slate-100">
                        <div className="font-medium text-slate-900 truncate max-w-[180px]">{e.firstName} {e.lastName}</div>
                        <div className="text-xs text-slate-500 truncate max-w-[180px]">{e.jobTitle || '—'}</div>
                      </td>
                      {skills.map(s => {
                        const lvl = matrix[e.id]?.[s.id] || 0;
                        return (
                          <td key={s.id} className="p-1 border-b border-slate-100">
                            <div
                              className={`w-8 h-8 rounded flex items-center justify-center mx-auto font-bold ${levelColor(lvl)}`}
                              title={`${e.firstName} ${e.lastName} · ${s.name} : niveau ${lvl}`}
                            >
                              {lvl > 0 ? lvl : '·'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2 mt-4 text-xs text-slate-500">
              <span>Niveaux :</span>
              {[0, 1, 2, 3, 4, 5].map(l => (
                <div key={l} className="flex items-center gap-1">
                  <span className={`w-5 h-5 rounded ${levelColor(l)} flex items-center justify-center font-bold`}>{l || '·'}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function CareerIntelligencePage() {
  const { user } = useAuth();
  const isAdmin = ['Admin', 'SuperAdmin'].includes(user?.role);
  const [employees, setEmployees] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plan');
  const [gapInitial, setGapInitial] = useState({ emp: '', profile: '' });
  const [aiCosts, setAiCosts] = useState({ costs: {}, balance: 0 });
  const [aiEmployee, setAiEmployee] = useState(null);
  const [aiDevPlan, setAiDevPlan] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [empRes, profRes, deptRes, famRes, costsRes] = await Promise.all([
          api.get('/employees?limit=2000'),
          api.get('/career-intel/job-profiles/lite'),
          api.get('/departments').catch(() => ({ data: [] })),
          api.get('/career/job-families').catch(() => ({ data: { items: [] } })),
          isAdmin ? api.get('/career-intel/ai/costs').catch(() => ({ data: { costs: {}, balance: 0 } })) : Promise.resolve({ data: { costs: {}, balance: 0 } }),
        ]);
        const empsList = (empRes.data?.employees || empRes.data || [])
          .filter(e => (e.status || 'Actif') === 'Actif')
          .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'fr'));
        setEmployees(empsList);
        setProfiles(profRes.data?.items || []);
        setDepartments(deptRes.data?.departments || deptRes.data || []);
        setFamilies(famRes.data?.items || []);
        setAiCosts(costsRes.data || { costs: {}, balance: 0 });
      } catch (e) {
        toast.error('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin]);

  const onShowGap = (emp, profile) => {
    setGapInitial({ emp, profile });
    setActiveTab('gap');
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="career-intel-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Brain className="w-6 h-6 text-indigo-600" />
            Career Intelligence
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Moteur de gap analysis, plan de carrière auto, matrice compétences — propulsé par les compétences évaluées.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="plan" data-testid="tab-trigger-plan"><Compass className="w-4 h-4 mr-2" />Plan de carrière</TabsTrigger>
            <TabsTrigger value="gap" data-testid="tab-trigger-gap"><Target className="w-4 h-4 mr-2" />Gap analysis</TabsTrigger>
            <TabsTrigger value="matrix" data-testid="tab-trigger-matrix"><Layers className="w-4 h-4 mr-2" />Matrice compétences</TabsTrigger>
          </TabsList>

          <TabsContent value="plan">
            {loading ? <p className="text-sm text-slate-400 text-center py-8">Chargement...</p> :
              <CareerPlanTab employees={employees} onShowGap={onShowGap}
                onAIEmployee={(empId) => setAiEmployee(empId)} isAdmin={isAdmin} />
            }
          </TabsContent>

          <TabsContent value="gap">
            {loading ? <p className="text-sm text-slate-400 text-center py-8">Chargement...</p> :
              <GapAnalysisTab employees={employees} profiles={profiles}
                initialEmp={gapInitial.emp} initialProfile={gapInitial.profile}
                onAIDevPlan={(emp, target) => setAiDevPlan({ emp, target })} isAdmin={isAdmin} />
            }
          </TabsContent>

          <TabsContent value="matrix">
            {loading ? <p className="text-sm text-slate-400 text-center py-8">Chargement...</p> :
              <SkillMatrixTab departments={departments} families={families} profiles={profiles} />
            }
          </TabsContent>
        </Tabs>

        {aiEmployee && (
          <AIInsightModal
            open={!!aiEmployee}
            onOpenChange={(v) => { if (!v) setAiEmployee(null); }}
            title="Analyse carrière (IA)"
            description="Synthèse IA des forces, axes de développement et recommandations stratégiques."
            endpoint={`/career-intel/ai/employee-analysis/${aiEmployee}`}
            costInfo={{ cost: aiCosts.costs?.career_employee_analysis?.cost, balance: aiCosts.balance }}
            resultField="analysis"
          />
        )}

        {aiDevPlan && (
          <AIInsightModal
            open={!!aiDevPlan}
            onOpenChange={(v) => { if (!v) setAiDevPlan(null); }}
            title="Plan de développement personnalisé (IA)"
            description="Plan en 3 phases avec actions concrètes et indicateurs de progression."
            endpoint={`/career-intel/ai/development-plan/${aiDevPlan.emp}/${aiDevPlan.target}`}
            costInfo={{ cost: aiCosts.costs?.career_development_plan?.cost, balance: aiCosts.balance }}
            resultField="plan"
          />
        )}
      </div>
    </DashboardLayout>
  );
}
